import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { ArrowLeft, ArrowRight, AlertCircle, Calendar, MapPin } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { createFormSubmission, formatFormSubmissionForChat } from '@/utils/formSubmissionUtils';
import { formatPhoneNumber } from '@/utils/phoneUtils';
import { getVehicleInterest } from '@/utils/interestDataUtils';
import { validateAge } from '@/utils/formValidation';
import { useDealerSession } from '@/contexts/DealerSessionContext';

interface FinanceFormProps {
  formData: { [key: string]: string };
  onUpdateData: (data: { [key: string]: string }) => void;
  currentStep: number;
  onStepChange: (step: number) => void;
  onSubmit?: (formData: { [key: string]: string }) => void;
  title?: string;
}

declare global {
  interface Window {
    google: any;
    initGooglePlaces: () => void;
  }
}

const FinanceForm: React.FC<FinanceFormProps> = ({
  formData,
  onUpdateData,
  currentStep,
  onStepChange,
  onSubmit,
}) => {
  const { t } = useLanguage();
  const { sessionData } = useDealerSession();
  const [hasVehicleInterest, setHasVehicleInterest] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const [localFormData, setLocalFormData] = useState(formData);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const totalSteps = hasVehicleInterest ? 4 : 6;
  const addressInputRef = useRef<HTMLInputElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  
  // Uncontrolled input refs for name and phone
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  
  // Sync local state when formData changes but stop once user starts typing
  useEffect(() => {
    if (!hasUserEdited) {
      setLocalFormData(formData);
    }
  }, [formData, hasUserEdited]);

  // Check for vehicle interest on component mount
  useEffect(() => {
    const vehicleInterest = getVehicleInterest();
    const hasInterest = vehicleInterest && Object.keys(vehicleInterest).length > 0;
    setHasVehicleInterest(hasInterest);
  }, []);

  const handleInputChange = (field: string, value: string) => {
    if (!hasUserEdited && (field === 'name' || field === 'phone')) {
      setHasUserEdited(true);
    }
    onUpdateData({ [field]: value });
  };

  const handleBudgetChange = (value: number[]) => {
    handleInputChange('monthlyBudget', value[0].toString());
  };

  const handleDateChange = (value: string) => {
    // Remove any non-digit characters except hyphens
    const cleanValue = value.replace(/[^\d-]/g, '');
    
    // Auto-format the date as YYYY-MM-DD
    let formattedValue = cleanValue;
    
    if (cleanValue.length >= 4 && !cleanValue.includes('-')) {
      // After 4 digits, add first hyphen
      formattedValue = cleanValue.slice(0, 4) + '-' + cleanValue.slice(4);
    }
    
    if (cleanValue.length >= 7 && cleanValue.split('-').length === 2) {
      // After 6 digits (YYYY-MM), add second hyphen
      const parts = formattedValue.split('-');
      if (parts[1] && parts[1].length >= 2) {
        formattedValue = parts[0] + '-' + parts[1].slice(0, 2) + '-' + cleanValue.slice(7);
      }
    }
    
    // Limit the total length to YYYY-MM-DD format
    if (formattedValue.length > 10) {
      formattedValue = formattedValue.slice(0, 10);
    }
    
    handleInputChange('dateOfBirth', formattedValue);
    
    // Real-time validation feedback
    if (formattedValue.length >= 8) {
      const validationError = validateAge(formattedValue, 18, t);
      setDateError(validationError);
    } else if (formattedValue.length > 0) {
      setDateError(t('form.validation.dateIncomplete'));
    } else {
      setDateError(null);
    }
    
    // Auto-advance cursor position after completing year (4 digits)
    if (dateInputRef.current && formattedValue.length === 5) {
      // Position cursor after the first hyphen (at month position)
      setTimeout(() => {
        if (dateInputRef.current) {
          dateInputRef.current.setSelectionRange(5, 5);
        }
      }, 0);
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      onStepChange(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      onStepChange(currentStep - 1);
    }
  };

  const handleFormSubmit = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const name = nameInputRef.current?.value || '';
      const phone = phoneInputRef.current?.value || '';
      
      const submissionData = {
        name,
        phone: formatPhoneNumber(phone),
        email: formData.email || '',
        dateOfBirth: formData.dateOfBirth || '',
        annualIncome: formData.annualIncome || '',
        address: formData.address || '',
        interest: formData.interest || '',
        vehicleOfInterest: formData.vehicleOfInterest || '',
        monthlyBudget: formData.monthlyBudget || ''
      };
      
      if (onSubmit) {
        const formSubmissionData = {
          ...submissionData,
          formType: 'FinanceV1'
        };
        
        await onSubmit(formSubmissionData);
      }
      
      onUpdateData({ ...formData, name, phone });
    } catch (error) {
      console.error('Finance form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    const name = nameInputRef.current?.value || '';
    const phone = phoneInputRef.current?.value || '';
    
    switch (currentStep) {
      case 0:
        return name.trim().length > 0 && phone.trim().length > 0;
      case 1:
        const emailValid = formData.email?.trim().length > 0 && formData.email.includes('@');
        const ageValid = validateAge(formData.dateOfBirth || '', 18, t) === null;
        return emailValid && ageValid;
      case 2:
        return formData.annualIncome?.trim().length > 0;
      case 3:
        if (hasVehicleInterest) {
          return formData.address?.trim().length > 0;
        } else {
          return formData.vehicleOfInterest?.trim().length > 0;
        }
      case 4:
        if (hasVehicleInterest) {
          return false;
        } else {
          return formData.monthlyBudget?.trim().length > 0;
        }
      case 5:
        return formData.address?.trim().length > 0;
      default:
        return false;
    }
  };

  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    if (numericValue) {
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
        minimumFractionDigits: 0
      }).format(parseInt(numericValue));
    }
    return '';
  };

  const handleIncomeChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    handleInputChange('annualIncome', numericValue);
  };

  // Render the current step
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-3">
            {/* Hidden Interest field */}
            <input type="hidden" name="interest" value={localFormData.interest || ''} />
            
            {/* Hidden topcarid field */}
            <input 
              id="topcarid" 
              name="topcarid" 
              type="hidden" 
              value={(() => {
                try {
                  const topCarIdElement = document.getElementById('topcarid') as HTMLInputElement;
                  return topCarIdElement?.value ? `Vehicle ID: ${topCarIdElement.value}` : '';
                } catch {
                  return '';
                }
              })()}
            />
            
            {/* Side by side fields */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="name" className="text-xs text-gray-700">{t('form.fullName')}</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  defaultValue={localFormData.name || ''}
                  ref={nameInputRef}
                  onChange={() => handleInputChange('name', nameInputRef.current?.value || '')}
                  placeholder={t('form.fullName')}
                  className="h-7 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="phone" className="text-xs text-gray-700">{t('form.phone')}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  defaultValue={localFormData.phone || ''}
                  ref={phoneInputRef}
                  onChange={() => handleInputChange('phone', phoneInputRef.current?.value || '')}
                  placeholder="(555) 555-1212"
                  className="h-7 text-sm"
                />
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-3">
            {/* Side by side fields */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="email" className="text-xs text-gray-700">{t('form.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="example@email.com"
                  className="h-7 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="dateOfBirth" className="text-xs text-gray-700">{t('form.dateOfBirth')}</Label>
                <div className="relative">
                  <Input
                    ref={dateInputRef}
                    id="dateOfBirth"
                    name="dateOfBirth"
                    type="text"
                    value={formData.dateOfBirth || ''}
                    onChange={(e) => handleDateChange(e.target.value)}
                    placeholder="YYYY-MM-DD"
                    className={`h-7 text-sm pr-8 ${dateError ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                    maxLength={10}
                    aria-invalid={dateError ? 'true' : 'false'}
                    aria-describedby={dateError ? 'dateOfBirth-error' : undefined}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                    {dateError ? (
                      <AlertCircle className="h-3 w-3 text-destructive" />
                    ) : (
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                    )}
                  </div>
                </div>
                {dateError && (
                  <div id="dateOfBirth-error" className="flex items-center gap-1 text-xs text-destructive" role="alert">
                    <AlertCircle className="h-3 w-3 flex-shrink-0" />
                    <span>{dateError}</span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {t('form.validation.ageRequirement')}
                </p>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-1">
            <Label htmlFor="annualIncome" className="text-xs text-gray-700">{t('form.annualIncome')}</Label>
            <div className="relative">
              <Input
                id="annualIncome"
                name="annualIncome"
                type="text"
                value={formatCurrency(formData.annualIncome || '')}
                onChange={(e) => handleIncomeChange(e.target.value)}
                placeholder="$75,000"
                className="h-7 text-sm"
              />
            </div>
          </div>
        );
      case 3:
        if (hasVehicleInterest) {
          return (
            <div className="space-y-1">
              <Label htmlFor="address" className="text-xs text-gray-700">{t('form.address')}</Label>
              <Input
                ref={addressInputRef}
                id="address"
                name="address"
                type="text"
                autoComplete="street-address"
                value={formData.address || ''}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder={t('form.addressPlaceholder')}
                className="h-7 text-sm"
              />
              <p className="text-xs text-gray-500">{t('form.addressHint')}</p>
            </div>
          );
        } else {
          return (
            <div className="space-y-1">
              <Label htmlFor="vehicleOfInterest" className="text-xs text-gray-700">{t('form.vehicleOfInterest')}</Label>
              <Input
                id="vehicleOfInterest"
                name="vehicleOfInterest"
                type="text"
                value={formData.vehicleOfInterest || ''}
                onChange={(e) => handleInputChange('vehicleOfInterest', e.target.value)}
                placeholder={t('form.vehicleOfInterestPlaceholder')}
                className="h-7 text-sm"
              />
            </div>
          );
        }
      case 4:
        if (!hasVehicleInterest) {
          return (
            <div className="space-y-2">
              <Label htmlFor="monthlyBudget" className="text-xs text-gray-700">{t('form.monthlyBudget')}</Label>
              <div className="px-2">
                <Slider
                  id="monthlyBudget"
                  min={100}
                  max={2000}
                  step={50}
                  value={[parseInt(formData.monthlyBudget || '500')]}
                  onValueChange={handleBudgetChange}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>$100</span>
                  <span className="font-medium">${formData.monthlyBudget || '500'}/month</span>
                  <span>$2000</span>
                </div>
              </div>
            </div>
          );
        }
        return null;
      case 5:
        if (!hasVehicleInterest) {
          return (
            <div className="space-y-1">
              <Label htmlFor="address" className="text-xs text-gray-700">{t('form.address')}</Label>
              <Input
                ref={addressInputRef}
                id="address"
                name="address"
                type="text"
                autoComplete="street-address"
                value={formData.address || ''}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder={t('form.addressPlaceholder')}
                className="h-7 text-sm"
              />
              <p className="text-xs text-gray-500">{t('form.addressHint')}</p>
            </div>
          );
        }
        return null;
      default:
        return null;
    }
  };

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <div className="p-2">
      <div className="mb-2">
        {renderStep()}
      </div>
      
      <div className="flex items-center justify-between">
        {!isFirstStep && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            className="h-6 px-2 text-xs"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            {t('form.back')}
          </Button>
        )}
        
        <div className="flex items-center space-x-1">
          {Array.from({ length: totalSteps }, (_, index) => (
            <div
              key={index}
              className={`w-1.5 h-1.5 rounded-full ${
                index === currentStep
                  ? 'bg-black'
                  : index < currentStep
                  ? 'bg-gray-400'
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        
        {!isLastStep ? (
          <Button
            type="button"
            size="sm"
            onClick={handleNext}
            disabled={!isStepValid()}
            className="h-6 px-2 text-xs bg-black hover:bg-gray-800"
          >
            {t('form.next')}
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={handleFormSubmit}
            disabled={!isStepValid() || isSubmitting}
            className="h-6 px-2 text-xs bg-black hover:bg-gray-800"
          >
            {isSubmitting ? t('form.submitting') : t('form.submit')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default FinanceForm;
